import {
  buildCategoryKey,
  buildCategoryParentKey,
} from "../library/utilization/CategoryKeyUtilization";

// The curriculum path of an outcome, and the THEME (ünite / tema) it sits under.
//
// A catalogue path runs program → grade program → theme → … → outcome ("MEB 2024 Fizik Programı" →
// "10. Sınıf Fizik Programı" → "KUVVET VE HAREKET" → …). The top two levels are the same for
// everything a learner sees, so the theme is the first level that says something.

/** How many top levels are shared by everything on screen (program, grade program). */
export const CATEGORY_SHARED_ANCESTOR_COUNT = 2;

export interface CategoryLookup {
  byId: Map<string, any>;
  byKey: Map<string, any>;
}

/**
 * Index the context category list once — it runs to ~9.7k rows for a full MEB profile, and every
 * resolved outcome walks up to 10 hops.
 */
export const buildCategoryLookup = (categoryList?: any[] | null): CategoryLookup => {
  const byId = new Map<string, any>();
  const byKey = new Map<string, any>();
  for (const category of categoryList || []) {
    if (category?.id != null) byId.set(String(category.id), category);
    byKey.set(buildCategoryKey(category), category);
  }
  return { byId, byKey };
};

/**
 * Root → leaf chain, walked up via parentCode within the leaf's OWN versionCode (see
 * CategoryKeyUtilization) and guarded against cycles/depth.
 */
export const buildCategoryPath = (leaf: any, lookup: CategoryLookup): any[] => {
  const path = [leaf];
  let current = leaf;
  let guard = 0;
  while (current?.parentCode && guard < 10) {
    const parent = lookup.byKey.get(buildCategoryParentKey(current));
    if (!parent || path.some((node) => node.id === parent.id)) break;
    path.unshift(parent);
    current = parent;
    guard += 1;
  }
  return path;
};

/**
 * The theme an outcome sits under, or undefined when its path is too shallow to have one (the
 * level under the shared ancestors IS the outcome) or the outcome is not in the loaded list.
 */
export const categoryThemeOf = (
  categoryId: number | string | null | undefined,
  lookup: CategoryLookup,
): any | undefined => {
  if (categoryId == null) return undefined;
  const leaf = lookup.byId.get(String(categoryId));
  if (!leaf) return undefined;
  const path = buildCategoryPath(leaf, lookup);
  return path.length > CATEGORY_SHARED_ANCESTOR_COUNT + 1
    ? path[CATEGORY_SHARED_ANCESTOR_COUNT]
    : undefined;
};

/**
 * The theme NAME of a bireysel ödev: the theme of its heaviest outcome that resolves. A round's
 * outcomes arrive as weighted `personalizedAssignmentCategoryDTOList` rows.
 */
export const personalizedAssignmentThemeNameOf = (
  personalizedAssignment: any,
  lookup: CategoryLookup,
): string | undefined => {
  const rows = [...(personalizedAssignment?.personalizedAssignmentCategoryDTOList || [])].sort(
    (first: any, second: any) => (second?.weight ?? 0) - (first?.weight ?? 0),
  );
  for (const row of rows) {
    const name = categoryThemeOf(row?.categoryId, lookup)?.name;
    if (name) return name;
  }
  return undefined;
};
